import { getImmutableMailId, type ImmutableMailIdKind } from '@data';
import { getOriginalMailId } from '@utils-global';

type ResolveActionMailIdParams = {
  isOnPremises: boolean;
  mailId: string | undefined;
  userEmail: string;
  isSharedMailbox?: boolean;
};

/**
 * Which kind of id the post-report action runs against, carried through to the
 * action so a failure can be read against the id that produced it.
 */
export type MailIdKind = ImmutableMailIdKind | 'ews';

type ResolvedActionMailId = {
  mailId: string;
  mailIdKind: MailIdKind;
};

/**
 * Resolves the mail id used for the post-report move/delete, capturing it while
 * the item is still in place. On cloud (Graph) it translates to an immutable id
 * that survives the item being relocated; on-premises (EWS) it keeps the EWS id.
 */
export const resolveActionMailId = async ({
  isOnPremises,
  mailId,
  userEmail,
  isSharedMailbox = false,
}: ResolveActionMailIdParams): Promise<ResolvedActionMailId | undefined> => {
  if (isOnPremises) {
    const ewsMailId = getOriginalMailId() || mailId;

    return ewsMailId ? { mailId: ewsMailId, mailIdKind: 'ews' } : undefined;
  }

  if (!mailId) {
    return undefined;
  }

  const { id, kind } = await getImmutableMailId(mailId, userEmail, isSharedMailbox);

  return { mailId: id, mailIdKind: kind };
};
